import { Link as ExpoLink, LinkProps } from "expo-router";
import React from "react";
import {
  GetProps,
  SizableText,
  Label as TamaguiLabel,
  Paragraph as TamaguiParagraph,
  styled,
} from "tamagui";

import { usePropagatedHref } from "@/hooks/usePropagatedRouter";
import withTracking from "./withTracking";

const DefaultFontVariants = Object.freeze({
  normal: {
    true: {
      fontWeight: 400 as const,
    },
  },
  medium: {
    true: {
      fontWeight: 500 as const,
    },
  },
  semibold: {
    true: {
      fontWeight: 600 as const,
    },
  },
  bold: {
    true: {
      fontWeight: 700 as const,
    },
  },
  serif: {
    true: {
      fontFamily: "$serif" as const,
    },
  },
  sansSerif: {
    true: {
      fontFamily: "$sans-serif" as const,
    },
  },
  italic: {
    true: {
      fontStyle: "italic" as const,
    },
  },
  conversational: {
    true: {
      fontFamily: "$serif" as const,
      lineHeight: 24 as const,
    },
  },
});

/**
 * When `size` or `fontFamily` are applied to a text element, the default properties are reapplied.
 * This helper generates variants that reapply the desired properties after changing the size or fontFamily.
 */
const ReapplyFontWeightVariants = (
  weight: keyof typeof DefaultFontVariants,
) => ({
  size: (
    size: string,
    { props: { fontFamily } }: { props: GetProps<typeof SizableText> },
  ) => ({
    fontFamily: fontFamily,
    fontSize: size,
    lineHeight: size,
    [weight]: true,
  }),
  fontFamily: (
    fontFamily: string,
    { props: { size } }: { props: GetProps<typeof SizableText> },
  ) => ({
    fontFamily,
    fontSize: size,
    lineHeight: size,
    [weight]: true,
  }),
});

export const Text = styled(SizableText, {
  fontFamily: "$sans-serif",
  color: "$textPrimary",
  display: "inline",
  variants: DefaultFontVariants,
});

export const Paragraph = styled(TamaguiParagraph, {
  whiteSpace: "pre-line",
  color: "$textSecondary",
  fontFamily: "$sans-serif",
  display: "inline",
  variants: DefaultFontVariants,
});

export const Time: React.FC<
  GetProps<typeof SizableText> & { dateTime: string }
> = styled(SizableText, {
  name: "Time",
  render: "time",
  fontFamily: "$sans-serif",
  variants: DefaultFontVariants,
});

/**
 * Heading component without any semantic size tagging.
 */
export const Heading = styled(Text, {
  name: "Heading",
  fontFamily: "$sans-serif",
  size: "$true",
  margin: 0,
  bold: true,
  variants: {
    ...DefaultFontVariants,
    ...ReapplyFontWeightVariants("bold"),
  },
});

export const Subheading = styled(Text, {
  fontFamily: "$sans-serif",
  size: "$true",
  margin: 0,
  medium: true,
  variants: ReapplyFontWeightVariants("bold"),
});

export const Label = styled(TamaguiLabel, {
  display: "block",
  fontFamily: "$sans-serif",
  size: "$sm",
  fontWeight: 500,
  color: "$textPrimary",
  padding: 0,
  selectable: true,
  variants: DefaultFontVariants,
});

export const H1 = styled(Heading, {
  name: "H1",
  render: "h1",
  size: "$xxl",
  $lg: {
    size: "$3xl",
  },
});

export const H2 = styled(Heading, {
  name: "H2",
  render: "h2",
  size: "$xl",
});

export const H3 = styled(Heading, {
  name: "H3",
  render: "h3",
  size: "$lg",
});

export const H4 = styled(Heading, {
  name: "H4",
  render: "h4",
  size: "$md",
});

export const H5 = styled(Heading, {
  name: "H5",
  render: "h5",
  size: "$sm",
});

export const LinkText = styled(Text, {
  name: "LinkText",
  color: "$textLink",
  cursor: "pointer",
  // NOTE: When size is applied, it resets the fontWeight to the default of the family
  // We want link to be bold, and heading is bold by default
  fontFamily: "$sans-serif",
  textDecorationLine: "underline",
  hoverStyle: {
    color: "$textLinkHover",
  },
  pressStyle: {
    color: "$textLinkHover",
  },
  focusVisibleStyle: {
    outlineOffset: 2,
    outlineColor: "$a11yBlue",
  },
});

const ExpoLinkWithTamagui = styled(ExpoLink, {
  name: "ExpoLinkWithTamagui",
});

const FormattedLink: React.FC<{
  href: LinkProps["href"];
  children: React.ReactNode;
  external?: boolean;
}> = ({ href, children, external = false, ...props }) => {
  const propagatedHref = usePropagatedHref(href);

  return React.isValidElement(children) ? (
    <ExpoLinkWithTamagui
      href={propagatedHref}
      {...(external && {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      })}
      {...props}
    >
      {children}
    </ExpoLinkWithTamagui>
  ) : (
    <ExpoLink
      href={propagatedHref}
      asChild
      {...(external && {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      })}
    >
      <LinkText {...props} render="a">
        {children}
      </LinkText>
    </ExpoLink>
  );
};

export const Link = withTracking(FormattedLink, "link");
